/**
 * MessageList Component
 * Lista messaggi con scroll automatico e lazy loading
 */

import { memo, useRef, useEffect, useLayoutEffect, useCallback, useMemo, useState } from 'react';
import { Loader2, MessageSquarePlus } from 'lucide-react';
import { MessageBubble } from './MessageBubble';
import { ImageGalleryGroup } from './ImageGalleryGroup';
import { DateSeparator } from './DateSeparator';
import { TypingIndicator } from './TypingIndicator';
import { useWhatsAppMessages } from '@/lib/hooks/whatsapp';
import { useMentionNames, fetchGroupParticipants } from '@/lib/hooks/whatsapp/useMentionNames';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useMessageSelection } from '../context/MessageSelectionContext';
import type { Message, ContactPresence } from '@/lib/types/whatsappChat';
import { debugLog } from '../chat/ChatDebugPanel';
import { PinnedBar } from './PinnedBar';
import { useChatCapabilities } from './useChatCapabilities';
import { useMessageMarksStore, keyOf as marksKeyOf } from './messageMarksStore';
import { useAttachmentStore } from '../input/attachmentStore';
import { filesFromList } from '../input/attachments';

interface MessageListProps {
  chatId: string;
  isGroup?: boolean;
  presence?: ContactPresence;
  onReply: (message: Message) => void;
  onMediaClick: (mediaUrl: string) => void;
  onForward?: (messageId: string) => void;
  onForwardToTeamChat?: (message: Message) => void;
  onRetry?: (tempId: string) => void;
  chatProfilePicUrl?: string;
  chatName?: string;
  searchQuery?: string;
  searchMatchIds?: string[];
  searchHighlightIndex?: number;
}

// Pattern messaggi skeleton con larghezze variabili
const SKELETON_PATTERNS = [
  { outbound: false, lines: [32, 48] },
  { outbound: true, lines: [40] },
  { outbound: false, lines: [56, 44, 36] },
  { outbound: true, lines: [52, 28] },
  { outbound: false, lines: [24] },
  { outbound: true, lines: [44, 36] },
  { outbound: false, lines: [40, 52] },
  { outbound: true, lines: [32] },
  { outbound: false, lines: [48, 40, 28] },
  { outbound: true, lines: [56] },
  { outbound: false, lines: [36] },
  { outbound: true, lines: [44, 32] },
  { outbound: false, lines: [52, 44] },
  { outbound: true, lines: [28] },
  { outbound: false, lines: [40] },
  { outbound: true, lines: [48, 36, 24] },
  { outbound: false, lines: [32, 44] },
  { outbound: true, lines: [52] },
  { outbound: false, lines: [44] },
  { outbound: true, lines: [36, 28] },
];

function MessageListSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-2 whatsapp-chat-bg flex flex-col justify-end">
      <div className="space-y-3 animate-pulse">
        {SKELETON_PATTERNS.map((pattern, i) => (
          <div key={i} className={`flex ${pattern.outbound ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`rounded-2xl px-4 py-3 max-w-[70%] shadow-sm ${
                pattern.outbound
                  ? 'bg-[#2383E2]/30'
                  : 'bg-white dark:bg-gray-800'
              }`}
            >
              {pattern.lines.map((width, j) => (
                <div
                  key={j}
                  className={`h-3 rounded ${j > 0 ? 'mt-2' : ''} ${
                    pattern.outbound
                      ? 'bg-[#2383E2]/40'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                  style={{ width: `${width * 4}px` }}
                />
              ))}
              <div
                className={`h-2 rounded w-12 mt-2 ml-auto ${
                  pattern.outbound
                    ? 'bg-[#2383E2]/40'
                    : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

type RenderItem =
  | { type: 'message'; msg: Message | { tempId: string } }
  | { type: 'gallery'; messages: Message[] };

/**
 * Raggruppa messaggi consecutivi di tipo image senza caption dallo stesso sender in gallery.
 * Max 6 immagini per gruppo.
 */
function groupConsecutiveImages(messages: (Message | { tempId: string })[]): RenderItem[] {
  const items: RenderItem[] = [];
  let i = 0;

  while (i < messages.length) {
    const msg = messages[i];
    // Controlla se è un messaggio immagine senza caption (non pending)
    if ('id' in msg && msg.type === 'image' && !msg.content && msg.mediaUrl) {
      const group: Message[] = [msg as Message];
      const direction = msg.direction;
      const senderJid = (msg as Message).senderJid;

      // Raccogli consecutive dello stesso sender
      let j = i + 1;
      while (j < messages.length && group.length < 6) {
        const next = messages[j];
        if (!('id' in next)) break; // pending message, stop
        if (next.type !== 'image' || next.content || !next.mediaUrl) break;
        if (next.direction !== direction) break;
        if (direction === 'inbound' && (next as Message).senderJid !== senderJid) break;
        group.push(next as Message);
        j++;
      }

      if (group.length >= 2) {
        items.push({ type: 'gallery', messages: group });
      } else {
        items.push({ type: 'message', msg: msg });
      }
      i = j;
    } else {
      items.push({ type: 'message', msg: msg });
      i++;
    }
  }

  return items;
}

function groupMessagesByDate(messages: (Message | { tempId: string })[]): Map<string, (Message | { tempId: string })[]> {
  const groups = new Map<string, (Message | { tempId: string })[]>();

  messages.forEach((msg) => {
    const date = 'timestamp' in msg ? msg.timestamp : new Date();
    const dateKey = date.toDateString();
    const group = groups.get(dateKey) || [];
    group.push(msg);
    groups.set(dateKey, group);
  });

  return groups;
}

export const MessageList = memo(function MessageList({
  chatId,
  isGroup = false,
  presence,
  onReply,
  onMediaClick,
  onForward,
  onForwardToTeamChat,
  onRetry,
  chatProfilePicUrl,
  chatName,
  searchQuery = '',
  searchMatchIds = [],
  searchHighlightIndex = -1,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);

  // Selection context
  const { isSelectionMode, selectedMessageIds, toggleMessageSelection, enterSelectionMode } = useMessageSelection();

  // Azioni disponibili per il numero attivo: calcolate una volta qui, non per ogni bolla.
  const liveCaps = useChatCapabilities();
  const capsKey = JSON.stringify(liveCaps);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const capabilities = useMemo(() => liveCaps, [capsKey]);

  // La ricerca parte dal risultato piu' recente: ChatView passa i risultati in ordine
  // cronologico con indice 0 al primo; qui li giriamo, cosi' "1 di N" e' l'ultimo
  // messaggio che contiene il testo (MessageSearchBar inverte coerentemente le frecce).
  const orderedMatchIds = useMemo(() => [...searchMatchIds].reverse(), [searchMatchIds]);

  // Refs per smart scroll - distingue tra nuovi messaggi e lazy loading
  const prevMessagesLengthRef = useRef(0);
  const prevLastMessageIdRef = useRef<string | null>(null);
  const prevLastTimestampRef = useRef<number | null>(null);
  const isInitialLoadRef = useRef(true);

  // Refs per scroll anchoring durante lazy loading
  const isLoadingOlderRef = useRef(false);
  const scrollHeightBeforeLoadRef = useRef(0);
  const prevLoadingRef = useRef(false);
  const wasAtBottomRef = useRef(true); // Traccia se l'utente era in fondo

  const { messages, loading, hasOlderMessages, loadOlderMessages, loadOlderError } = useWhatsAppMessages(chatId);
  // P2: Selettore individuale
  const wahaConfig = useWhatsAppChatStore(s => s.wahaConfig);
  const loadMarks = useMessageMarksStore(s => s.load);
  const resetMarks = useMessageMarksStore(s => s.reset);
  // Chiave dei segni della chat aperta: la barra "Messaggio fissato" mostra solo quelli di questa chat.
  const marksKey = wahaConfig?.configId && chatId ? marksKeyOf({ configId: wahaConfig.configId, chatJid: chatId }) : null;

  // Messaggi fissati e importanti della chat (solo numeri Standard: Cloud non li ha).
  // A ogni cambio di chat o di numero si svuota tutto (load svuota prima di leggere).
  useEffect(() => {
    if (!wahaConfig?.configId || !wahaConfig.organizationId || !chatId || capabilities.isCloud) {
      resetMarks();
      return;
    }
    loadMarks({ configId: wahaConfig.configId, organizationId: wahaConfig.organizationId, chatJid: chatId });
  }, [chatId, wahaConfig?.configId, wahaConfig?.organizationId, capabilities.isCloud, loadMarks, resetMarks]);

  // Safe messages array
  const safeMessages = messages || [];

  // Risolvi nomi menzioni @NUMERO via WAHA API (solo per gruppi)
  const mentionMessagesForHook = useMemo(
    () => safeMessages.filter((m): m is Message => 'content' in m && !!m.content).map(m => ({ content: m.content })),
    [safeMessages]
  );
  const resolvedMentionNames = useMentionNames(mentionMessagesForHook, isGroup, chatId);

  // Fetch SEMPRE i partecipanti del gruppo per risolvere sender names (indipendente dalle menzioni)
  const [groupParticipantsMap, setGroupParticipantsMap] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (!isGroup || !chatId || !wahaConfig) return;
    let cancelled = false;
    fetchGroupParticipants(chatId).then((map) => {
      if (!cancelled && map.size > 0) setGroupParticipantsMap(map);
    });
    return () => { cancelled = true; };
  }, [chatId, isGroup, wahaConfig]);

  // Merge: partecipanti gruppo (per sender names) + menzioni risolte (priorità per LID)
  const mergedNamesMap = useMemo(() => {
    if (groupParticipantsMap.size === 0 && resolvedMentionNames.size === 0) return new Map<string, string>();
    const merged = new Map(groupParticipantsMap);
    for (const [k, v] of resolvedMentionNames) {
      merged.set(k, v);
    }
    return merged;
  }, [groupParticipantsMap, resolvedMentionNames]);

  // Debug log in useEffect (non nel useMemo, altrimenti causa setState durante render)
  useEffect(() => {
    if (isGroup && mergedNamesMap.size > 0) {
      debugLog('MessageList', `🗂️ mergedNamesMap: ${mergedNamesMap.size} entries (${groupParticipantsMap.size} partecipanti + ${resolvedMentionNames.size} menzioni)`,
        Object.fromEntries(Array.from(mergedNamesMap.entries()).slice(0, 10)), 'data');
    }
  }, [mergedNamesMap, isGroup, groupParticipantsMap.size, resolvedMentionNames.size]);

  // Handle selection toggle (needs messages array for shift+click range selection)
  const handleSelectionToggle = useCallback((messageId: string, shiftKey: boolean) => {
    toggleMessageSelection(messageId, safeMessages as Message[], shiftKey);
  }, [toggleMessageSelection, safeMessages]);

  // Voce "Seleziona" del menu: apre la selezione multipla con questo messaggio gia' scelto.
  const handleStartSelection = useCallback((messageId: string) => {
    enterSelectionMode();
    toggleMessageSelection(messageId, safeMessages as Message[], false);
  }, [enterSelectionMode, toggleMessageSelection, safeMessages]);

  // Typing indicator - calcolato direttamente come ChatHeader (no stato interno)
  const isTyping = presence?.status === 'typing' || presence?.status === 'recording';

  // Reset refs quando cambia chat. DEVE stare prima dell'auto-scroll: gli effetti girano
  // nell'ordine in cui sono dichiarati. Prima era un useEffect dopo il layout effect: con i
  // messaggi gia' in memoria l'auto-scroll vedeva ancora lo stato della chat precedente e la
  // chat si riapriva a meta' (test del 25/09/2026).
  useLayoutEffect(() => {
    isInitialLoadRef.current = true;
    prevMessagesLengthRef.current = 0;
    prevLastMessageIdRef.current = null;
    prevLastTimestampRef.current = null;
    isLoadingOlderRef.current = false;
    prevLoadingRef.current = true; // Assume loading starts
    wasAtBottomRef.current = true; // Assume al fondo per nuova chat
  }, [chatId]);

  // Foto, anteprime e vocali si caricano dopo il primo disegno e allungano la lista: se eri
  // in fondo, resti in fondo. Se stavi leggendo piu' su, non ti sposta.
  // `load` non risale il DOM: si ascolta in fase di cattura (img, video, iframe).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onMediaLoaded = () => {
      if (wasAtBottomRef.current && !isLoadingOlderRef.current) {
        container.scrollTop = container.scrollHeight;
      }
    };
    container.addEventListener('load', onMediaLoaded, true);
    container.addEventListener('loadedmetadata', onMediaLoaded, true);
    // La lista si accorcia quando sotto compaiono la barra "rispondi a", l'anteprima
    // AI o il conto alla rovescia dell'invio: senza questo l'ultimo messaggio restava coperto.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onMediaLoaded) : null;
    ro?.observe(container);
    return () => {
      container.removeEventListener('load', onMediaLoaded, true);
      container.removeEventListener('loadedmetadata', onMediaLoaded, true);
      ro?.disconnect();
    };
  }, [chatId, loading]);

  // Smart auto-scroll - scrolla solo per NUOVI messaggi, non per lazy loading
  // useLayoutEffect per eseguire lo scroll anchoring PRIMA del paint del browser
  useLayoutEffect(() => {
    const container = containerRef.current;
    const prevLength = prevMessagesLengthRef.current;
    const currentLength = safeMessages.length;
    const lastMessage = safeMessages.length > 0 ? safeMessages[safeMessages.length - 1] : null;
    const lastMessageId = lastMessage ? ('id' in lastMessage ? lastMessage.id : (lastMessage as { tempId?: string }).tempId) : null;
    const lastTimestamp = lastMessage && 'timestamp' in lastMessage ? lastMessage.timestamp.getTime() : null;

    const prevLastTimestamp = prevLastTimestampRef.current;

    // Primo caricamento - scroll con requestAnimationFrame per garantire DOM ready
    if (isInitialLoadRef.current && currentLength > 0) {
      isInitialLoadRef.current = false;
      prevMessagesLengthRef.current = currentLength;
      prevLastMessageIdRef.current = lastMessageId || null;
      prevLastTimestampRef.current = lastTimestamp;

      // Usa requestAnimationFrame per garantire che il DOM sia completamente renderizzato
      requestAnimationFrame(() => {
        if (bottomRef.current) {
          bottomRef.current.scrollIntoView({ behavior: 'instant' });
        }
      });
      return;
    }

    // Scroll anchoring: se abbiamo caricato messaggi vecchi, mantieni la posizione
    if (isLoadingOlderRef.current && container && currentLength > prevLength) {
      const scrollHeightAfter = container.scrollHeight;
      const scrollDiff = scrollHeightAfter - scrollHeightBeforeLoadRef.current;
      container.scrollTop = scrollDiff;
      isLoadingOlderRef.current = false;
      prevMessagesLengthRef.current = currentLength;
      // Aggiorna anche timestamp/id per evitare false detection di "nuovo messaggio"
      prevLastMessageIdRef.current = lastMessageId || null;
      prevLastTimestampRef.current = lastTimestamp;
      return;
    }

    // Verifica se c'è un NUOVO messaggio (in fondo, non in alto)
    const hasNewMessage = currentLength > prevLength && lastTimestamp !== null && prevLastTimestamp !== null;

    if (hasNewMessage && lastTimestamp > prevLastTimestamp) {
      // Nuovo messaggio recente - scroll to bottom
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (currentLength > 0 && prevLastTimestamp === null && lastTimestamp !== null) {
      // Edge case: prima volta che vedi un messaggio
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }

    // Salva stato corrente
    prevMessagesLengthRef.current = currentLength;
    prevLastMessageIdRef.current = lastMessageId || null;
    prevLastTimestampRef.current = lastTimestamp;
  }, [safeMessages]);

  // Traccia se l'utente è in fondo allo scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const threshold = 100; // px dal fondo
      const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
      wasAtBottomRef.current = isAtBottom;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll quando loading finisce con messaggi (per backfill WAHA o caricamento iniziale)
  // Scrolla in basso SOLO se l'utente era già in fondo prima del caricamento
  // NON scrollare se il loading era da loadOlderMessages (gestito dallo scroll anchoring)
  useEffect(() => {
    if (prevLoadingRef.current && !loading && safeMessages.length > 0
        && wasAtBottomRef.current && !isLoadingOlderRef.current) {
      requestAnimationFrame(() => {
        if (bottomRef.current) {
          bottomRef.current.scrollIntoView({ behavior: 'instant' });
        }
      });
    }
    prevLoadingRef.current = loading;
  }, [loading, safeMessages.length]);

  // Reset isLoadingOlderRef quando loading torna false
  // IMPORTANTE: Reset sincrono senza setTimeout per evitare race conditions
  // Il setTimeout precedente veniva cancellato se loading cambiava velocemente
  useEffect(() => {
    if (!loading) {
      isLoadingOlderRef.current = false;
    }
  }, [loading, safeMessages.length]);

  // Callback per caricare messaggi più vecchi con scroll anchoring
  const handleLoadOlder = useCallback(() => {
    const container = containerRef.current;
    if (!container || loading || isLoadingOlderRef.current) return;

    // Salva la scroll height prima del caricamento
    scrollHeightBeforeLoadRef.current = container.scrollHeight;
    isLoadingOlderRef.current = true;
    // Forza wasAtBottom a false: l'utente sta caricando messaggi vecchi,
    // NON deve scrollare in basso quando il loading finisce
    wasAtBottomRef.current = false;
    loadOlderMessages();
  }, [loading, loadOlderMessages]);

  // Lazy loading observer
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasOlderMessages && !loading && !isLoadingOlderRef.current) {
          handleLoadOlder();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasOlderMessages, loading, handleLoadOlder]);

  // Scroll to current search match when navigating
  useEffect(() => {
    if (searchHighlightIndex < 0 || orderedMatchIds.length === 0) return;
    const targetId = orderedMatchIds[searchHighlightIndex];
    if (!targetId) return;
    const el = document.getElementById(`msg-${targetId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [searchHighlightIndex, orderedMatchIds]);

  // Auto-scroll when typing indicator appears
  useEffect(() => {
    if (isTyping && bottomRef.current && wasAtBottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isTyping]);

  // Trascina file sulla chat: vanno nell'anteprima allegati della barra di scrittura.
  const [isDragOver, setIsDragOver] = useState(false);
  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (isSelectionMode || !e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    setIsDragOver(true);
  }, [isSelectionMode]);
  const handleDrop = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    setIsDragOver(false);
    useAttachmentStore.getState().add(chatId, filesFromList(e.dataTransfer.files));
  }, [chatId]);

  // Memoize grouping per evitare ricalcolo ad ogni render
  const messageGroups = useMemo(() => groupMessagesByDate(safeMessages), [safeMessages]);

  if (loading && safeMessages.length === 0) {
    return <MessageListSkeleton />;
  }

  return (
    <div
      ref={containerRef}
      className={`relative flex-1 overflow-y-auto overflow-x-hidden px-4 pt-2 pb-6 min-w-0 whatsapp-chat-bg ${isDragOver ? 'ring-2 ring-inset ring-[#25D366]' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false); }}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="sticky top-1/2 z-20 flex justify-center pointer-events-none">
          <span className="px-4 py-2 rounded-full bg-white/95 dark:bg-gray-800/95 shadow text-sm font-medium text-[#128C7E]">Rilascia qui per allegare i file</span>
        </div>
      )}
      {/* Messaggio fissato in alto */}
      {!capabilities.isCloud && (
        <PinnedBar messages={safeMessages.filter((m): m is Message => 'id' in m)} canUnpin={capabilities.canPin} marksKey={marksKey} />
      )}

      {/* Top sentinel for lazy loading */}
      <div ref={topSentinelRef} className="h-1" />

      {/* Chat nuova, ancora senza messaggi */}
      {!loading && safeMessages.length === 0 && !loadOlderError && (
        <div className="flex flex-col items-center justify-center text-center gap-2 py-16 px-6">
          <MessageSquarePlus className="w-8 h-8 text-[#A4A4A0]" aria-hidden />
          <p className="text-sm font-medium text-[#37352F] dark:text-white/90">Nessun messaggio in questa chat</p>
          <p className="text-xs text-[#787774] max-w-xs">Scrivi il primo messaggio nel campo qui sotto: arrivera' su WhatsApp a questo contatto.</p>
        </div>
      )}

      {/* Loading indicator for older messages */}
      {loading && safeMessages.length > 0 && (
        <div className="flex justify-center py-2">
          <Loader2 className="w-5 h-5 text-[#2383E2] animate-spin" />
        </div>
      )}

      {/* Error message for older messages */}
      {loadOlderError && (
        <div className="flex justify-center py-2">
          <span className="text-xs text-red-500">{loadOlderError}</span>
        </div>
      )}

      {/* Messages grouped by date */}
      {Array.from(messageGroups.entries()).map(([dateKey, msgs]) => {
        const renderItems = groupConsecutiveImages(msgs);
        return (
          <div key={dateKey}>
            <DateSeparator date={new Date(dateKey)} />
            <div className="space-y-2">
              {renderItems.map((item) => {
                if (item.type === 'gallery') {
                  const galleryKey = item.messages.map(m => m.id).join('-');
                  return (
                    <div key={galleryKey}>
                      <ImageGalleryGroup
                        messages={item.messages}
                        isGroup={isGroup}
                        onImageClick={onMediaClick}
                      />
                    </div>
                  );
                }
                const msg = item.msg;
                const messageId = 'id' in msg ? msg.id : (msg as { tempId: string }).tempId;
                const isSearchMatch = searchQuery && searchMatchIds.includes(messageId);
                const isCurrentMatch = isSearchMatch && orderedMatchIds[searchHighlightIndex] === messageId;
                return (
                  <div
                    key={messageId}
                    id={`msg-${messageId}`}
                    className={isCurrentMatch ? 'ring-2 ring-[#2383E2]/50 rounded-xl' : isSearchMatch ? 'ring-1 ring-[#2383E2]/25 rounded-xl' : undefined}
                  >
                    <MessageBubble
                      message={msg as Message}
                      isGroup={isGroup}
                      onReply={onReply}
                      onMediaClick={onMediaClick}
                      onForward={onForward}
                      onForwardToTeamChat={onForwardToTeamChat}
                      onRetry={onRetry}
                      chatProfilePicUrl={chatProfilePicUrl}
                      chatName={chatName}
                      resolvedMentionNames={mergedNamesMap}
                      isSelectionMode={isSelectionMode}
                      isSelected={selectedMessageIds.has(messageId)}
                      onSelectionToggle={handleSelectionToggle}
                      onStartSelection={handleStartSelection}
                      capabilities={capabilities}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Typing indicator */}
      {isTyping && <TypingIndicator isVisible={true} />}

      {/* Bottom anchor for scroll */}
      <div ref={bottomRef} />
    </div>
  );
});
